import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { EMPTY, Observable } from 'rxjs';
import { switchMap, timeout } from 'rxjs/operators';
import { global } from '../global';
import { MT5_GATEWAY_TOKEN } from './meta-api.service';

/** GET /gateway/status del Nectum MT5 Gateway local. */
export interface Mt5GatewayStatus {
  gwVersion: string;
  terminalInstalled: boolean;
  initialized: boolean;
  /** Terminal abierto, con cuenta iniciada y conectado al servidor del bróker */
  terminalConnected: boolean;
  tradeAllowed: boolean;
  /** Botón "Algo Trading" del terminal: sin él MT5 rechaza las órdenes */
  algoTradingEnabled: boolean;
  /** Id para las rutas /users/current/accounts/{accountId}/… (mt5gw-{login}-{servidor}) */
  accountId: string | null;
  login: number | null;
  server: string | null;
  company: string | null;
  name: string | null;
  currency: string | null;
  brokerUtcOffsetMin: number;
  brokerOffsetSource: 'mql5' | 'tick' | 'saved' | 'default';
  sessionsSource: 'mql5' | 'inferred';
  sessionsServiceInstalled: boolean;
  /** Carpeta del terminal conectado y terminal64.exe elegido (null = el que elija la librería) */
  terminalPath: string | null;
  selectedTerminalPath: string | null;
  lastError: string | null;
}

/** GET /gateway/terminals: terminales MT5 instalados en el equipo (el gateway no los abre). */
export interface Mt5Terminal {
  /** Ruta de terminal64.exe: es lo que se envía a POST /gateway/terminal */
  path: string;
  name: string;
  running: boolean;
  /** Cuenta conectada (terminal activo) o última usada (resto) */
  lastLogin: number | null;
  lastServer: string | null;
  lastAccountId: string | null;
  /** Terminal al que está conectado el gateway ahora */
  active: boolean;
  selected: boolean;
}

/**
 * Endpoints propios del gateway local (estado y login). El resto (cuenta, posiciones, órdenes, trade,
 * velas…) es la misma REST que MetaAPI y lo sirve MetaApiService con el origen 'GATEWAY'.
 */
@Injectable({
  providedIn: 'root'
})
export class Mt5GatewayService {

  private readonly baseUrl = global.session.mt5GwBaseUrl;

  constructor(private http: HttpClient, private cookieService: CookieService) {}

  private headers(): HttpHeaders {
    return new HttpHeaders({ 'auth-token': MT5_GATEWAY_TOKEN, 'Content-Type': 'application/json' });
  }

  /** Error con status 0: gateway no instalado/parado o permiso de red local del navegador sin conceder. */
  getStatus(): Observable<Mt5GatewayStatus> {
    return this.http.get<Mt5GatewayStatus>(`${this.baseUrl}/gateway/status`, { headers: this.headers() })
      .pipe(timeout(35_000));
  }

  /** Inicia sesión en el terminal. Las credenciales van solo al gateway local; no pasan por el backend. */
  login(login: string | number, password: string, server: string): Observable<Mt5GatewayStatus> {
    return this.http.post<Mt5GatewayStatus>(
      `${this.baseUrl}/gateway/login`,
      { login: String(login).trim(), password, server: server.trim() },
      { headers: this.headers() }
    ).pipe(timeout(95_000));
  }

  getTerminals(): Observable<Mt5Terminal[]> {
    return this.http.get<Mt5Terminal[]>(`${this.baseUrl}/gateway/terminals`, { headers: this.headers() })
      .pipe(timeout(35_000));
  }

  /**
   * Conecta el gateway a otro terminal (con varios MT5 instalados) y, si se indican login y servidor,
   * a esa cuenta con la contraseña guardada en el terminal. El gateway recuerda la elección.
   */
  selectTerminal(path: string, login?: number | null, server?: string | null): Observable<Mt5GatewayStatus> {
    const body: any = { path };
    if (login) body.login = login;
    if (server) body.server = server;
    return this.http.post<Mt5GatewayStatus>(`${this.baseUrl}/gateway/terminal`, body, { headers: this.headers() })
      .pipe(timeout(95_000));
  }

  installerUrl(): string {
    return global.session.mt5GwInstaller;
  }

  /**
   * Registra (o refresca) en el backend la cuenta abierta en el gateway: tbl_metaapi_cuenta con
   * origen GATEWAY y el offset del bróker, que el backend no puede medir porque no llega a localhost.
   * Responde { status, metaApiAccountId } o { status: false, error }.
   */
  registrarEnBackend(estado: Mt5GatewayStatus): Observable<any> {
    const licencia = this.cookieService.get('auth_token');
    return this.http.post(`${global.url}metaapi/registrar-gateway`, {
      login: String(estado.login),
      server: estado.server,
      nombre: estado.name ? `${estado.login} - ${estado.name}` : `MT5-${estado.login}`,
      brokerUtcOffsetMin: estado.brokerUtcOffsetMin
    }, { headers: new HttpHeaders({ 'licencia': licencia, 'Content-Type': 'application/json' }) });
  }

  /** Al abrir la cartera: vuelve a guardar el offset del bróker si el terminal sigue en esa cuenta. */
  sincronizarOffset(accountId: string): Observable<any> {
    return this.getStatus().pipe(
      switchMap(estado => estado.terminalConnected && estado.accountId === accountId
        ? this.registrarEnBackend(estado)
        : EMPTY)
    );
  }
}
